import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';

import { GeopostCheckbox } from 'components/geopostCheckbox/GeopostCheckbox';
import { FeatureGeom } from 'features/seismic-3d/models/classes/FeatureGeom';
import { Seismic3DData } from 'features/seismic-3d/models/classes/Seismic3DData';
import { use3DGridStore } from 'features/seismic-3d/stores/use3DGridStore';
import { IntersectingSeismicCheckbox } from './IntersectingSeismicsList/IntersectingSeismicCheckbox';
import { SeismicsListRow } from './SeismicsListRow';
import { useMetadata3DViewer } from 'features/seismic-3d/api/useVolume';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';

type Props = {
    onDisplayShape: (seismicData: Seismic3DData, seismicGeom: FeatureGeom) => void,
    onHideShape: (seismic3DData: Seismic3DData) => void,
    seismics: Seismic3DData[],
    selectedSeismics: Seismic3DData[],
    isLoadingCallback: (Seismic3DData: Seismic3DData) => boolean,
    onToggleGrid: (product: Seismic3DData, checked: boolean) => void,
};

export const SeismicsList = ({ onDisplayShape, onHideShape, seismics, selectedSeismics, isLoadingCallback, onToggleGrid } : Props) => {
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            border: '1px solid #d9d9d9',
            whiteSpace: 'nowrap'
        },
        [`&.${tableCellClasses.body}`]: {
            border: '1px solid #d9d9d9'
        },
    }));

    const { selectedVolumes, addSelectedVolume, removeSelectedVolume } = use3DGridStore(state => ({
        selectedVolumes: state.selectedVolumes,
        addSelectedVolume: state.addSelectedVolume,
        removeSelectedVolume: state.removeSelectedVolume,
    }));
    const { current: urlParams } = useRef(getParamsFromUrl());
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(5);

    const visibleRows = useMemo(() => {
        return seismics.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        );
    }, [page, rowsPerPage, seismics]);

    return (
        <React.Fragment>
            <TableContainer
                sx={{mt: '5px', '& > *': { borderBottom: 'unset' }}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {/*
                            <TableCell size='small'>
                                Details
                            </TableCell>
                            */}
                            <TableCell size='small'>
                                Display Geometry
                            </TableCell>
                            <TableCell size='small' sx={{whiteSpace: 'nowrap'}}>
                                Display Data
                            </TableCell>
                            <TableCell size='small'>
                                Survey/Line
                            </TableCell>
                            <TableCell size='small'>
                                Product
                            </TableCell>
                            <TableCell size='small'>Type</TableCell>
                            <TableCell size='small'>Domain</TableCell>
                            <TableCell size='small'>Traces number</TableCell>
                            <TableCell size='small'>Samples per trace</TableCell>
                            <TableCell size='small'>Sample Interval</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows && visibleRows.map((seismic, index) => (
                            <SeismicsListRow
                                seismic={seismic}
                                key={index}
                                checked={!!selectedSeismics.find(selectedSeismic => selectedSeismic.volumeToken === seismic.volumeToken)}
                                onSelect={onDisplayShape}
                                onUnselect={() => onHideShape(seismic)}
                                onGridCheckedChange={checked => onToggleGrid(seismic, checked)}
                                gridChecked={!!selectedVolumes.find(x => x.VolumeToken === seismic.volumeToken)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component='div'
                rowsPerPageOptions={[5, 10, 15]}
                count={seismics.length}
                page={page}
                onPageChange={(evt, page) => setPage(page)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(evt) => setRowsPerPage(parseInt(evt.target.value)) }
            />
        </React.Fragment>
    );
};